import { useDebounceFn } from 'ahooks'
import { Spin } from 'antd'
import { Trans } from '@lingui/react/macro'

import VoiceCard from '../VoiceCard'

import VoiceCategoryList from './VoiceCategoryList'
import VoiceSearchInput from './VoiceSearchInput'

import type { VoiceCategory, VoiceItem } from '../../../_types/form'

import TipsBaseModal from '@/pages/tad.ai/_blocks/Modals/Tips/TipsBaseModal'
import ScrollArea from '@/pages/tad.ai/_components/ScrollArea'
import { CssIcon } from '@/components/CssIcon'


import { useState, useEffect } from 'react';
import { cls } from 'tagged-classnames-free';
interface VoiceModalProps {
  open: boolean
  onClose: () => void
  categories: VoiceCategory[]
  isLoading: boolean
  selectedVoiceId: string
  onSelect: (voice: VoiceItem) => void
  searchVoices: (keyword: string) => VoiceItem[]
  /** Voice player state */
  currentPlayingId: string | null
  currentSourceId: string | null
  isPlaying: boolean
  playLoading: boolean
  onTogglePlay: (voice: VoiceItem) => void
}

function VoiceModal(props: VoiceModalProps) {
  const {
    open,
    onClose,
    categories,
    isLoading,
    selectedVoiceId,
    onSelect,
    searchVoices,
    currentPlayingId,
    currentSourceId,
    isPlaying,
    playLoading,
    onTogglePlay,
  } = props

  const [activeCategory, setActiveCategory] = useState('')
  const [searchValue, setSearchValue] = useState('')
  const [searchResults, setSearchResults] = useState<VoiceItem[] | null>(null)
  const [isSearching, setIsSearching] = useState(false)

  // Current category's voices
  const activeCategoryData = categories.find((c) => c.key === activeCategory)
  const currentVoices = activeCategoryData?.voices ?? []

  // Default select first category
  useEffect(() => {
    if (categories.length > 0 && !activeCategory) {
      setActiveCategory(categories[0].key)
    }
  }, [categories, activeCategory])

  // Reset on open
  useEffect(() => {
    if (open) {
      setSearchValue('')
      setSearchResults(null)
      setIsSearching(false)
    }
  }, [open])

  // Search (300ms debounce)
  const { run: doSearch, cancel: cancelSearch } = useDebounceFn(
    (keyword: string) => {
      if (!keyword.trim()) {
        setSearchResults(null)
        setIsSearching(false)
        return
      }
      const results = searchVoices(keyword)
      setSearchResults(results)
      setIsSearching(false)
    },
    { wait: 300 },
  )

  const handleSearchChange = (value: string) => {
    setSearchValue(value)
    if (!value.trim()) {
      cancelSearch()
      setSearchResults(null)
      setIsSearching(false)
      return
    }
    setIsSearching(true)
    doSearch(value)
  }

  const isModalActive = currentSourceId === 'modal'

  const renderVoiceList = (voices: VoiceItem[]) => (
    <div className={cls`grid grid-cols-1 gap-3 pt-2 md:grid-cols-2`}>
      {voices.map((voice) => (
        <VoiceCard
          key={voice.id}
          voice={voice}
          selected={selectedVoiceId === voice.id}
          playing={isModalActive && currentPlayingId === voice.id && isPlaying}
          loading={
            isModalActive && currentPlayingId === voice.id && playLoading
          }
          onSelect={() => onSelect(voice)}
          onTogglePlay={() => onTogglePlay(voice)}
        />
      ))}
    </div>
  )

  const renderContent = () => {
    if (isLoading || isSearching) {
      return (
        <div
          className={cls`flex min-h-full flex-1 items-center justify-center`}
        >
          <Spin />
        </div>
      )
    }

    if (searchResults !== null) {
      if (searchResults.length === 0) {
        return (
          <div
            className={cls`flex min-h-full flex-1 flex-col items-center justify-center gap-3`}
          >
            <CssIcon
              className={cls`i-cus--tad-no-linear text-f-text-quaternary size-12`}
            />
            <div
              className={cls`text-f-text-quaternary text-center text-sm leading-relaxed`}
            >
              <Trans>
                We couldn't find any results. Please try different keywords.
              </Trans>
            </div>
          </div>
        )
      }
      return renderVoiceList(searchResults)
    }

    return renderVoiceList(currentVoices)
  }

  return (
    <TipsBaseModal
      open={open}
      onCancel={onClose}
      destroyOnHidden
      width={960}
      maskClosable
      closable={false}
      className='[&_.coco-modal-content]:p-5'
    >
      <div className={cls`flex flex-col gap-4`}>
        {/* Header: title + search + close */}
        <div className={cls`flex items-center justify-between gap-2`}>
          <span className={cls`text-f-text text-lg font-semibold`}>
            <Trans>Voice Library</Trans>
          </span>
          <div className={cls`flex shrink-0 items-center gap-2`}>
            <VoiceSearchInput
              value={searchValue}
              onChange={handleSearchChange}
              className={cls`hidden w-64 md:flex`}
            />
            <button
              type='button'
              className={cls`text-f-text-quaternary hover:text-f-text flex size-6 cursor-pointer items-center justify-center`}
              onClick={onClose}
            >
              <CssIcon className={cls`i-com--x size-5`} />
            </button>
          </div>
        </div>

        {/* Mobile search */}
        <div className={cls`md:hidden`}>
          <VoiceSearchInput
            value={searchValue}
            onChange={handleSearchChange}
            className={cls`w-full`}
          />
        </div>

        {/* Body: category sidebar + voice grid */}
        <div className={cls`flex h-[45vh] md:h-[346px]`}>
          {/* Left: category list */}
          {!searchValue && (
            <ScrollArea
              scrollbarVisibility='hoverOrScroll'
              className={cls`border-f-border-secondary max-w-[100px] shrink-0 border-r pr-4 md:max-w-[180px]`}
            >
              <VoiceCategoryList
                categories={categories}
                activeKey={activeCategory}
                onChange={setActiveCategory}
              />
            </ScrollArea>
          )}

          {/* Right: voice cards */}
          <div
            className={cls`flex min-w-0 flex-1 flex-col ${!searchValue ? 'pl-4' : ''}`}
          >
            <ScrollArea
              scrollbarVisibility='hoverOrScroll'
              className={cls`min-h-0 flex-1`}
              classNames={{ content: 'min-h-full flex flex-col' }}
            >
              {renderContent()}
            </ScrollArea>
          </div>
        </div>
      </div>
    </TipsBaseModal>
  )
}

export default VoiceModal
